import { create } from 'zustand';
import type { AuthUser } from '../types';
import { queryClient } from '../queryClient';
import { usePaperTradingStore } from './paperTradingStore';
import { useBacktestStore } from './backtestStore';
import { useConfigStore } from './configStore';
import { useConfigurationStore } from './configurationStore';
import { useAggregationStore } from './aggregationStore';
import { useSettingsStore } from './settingsStore';
import { usePortfoliosStore } from './portfoliosStore';
import { useOptimizationStore } from './optimizationStore';
import { useOptimizerModalStore } from './optimizerModalStore';
import { useRunBacktestModalStore } from './runBacktestModalStore';
import { useScannerStore } from './scannerStore';
import { useRealtimeEquityStore } from './realtimeEquityStore';
import { useAgentStatusStore } from './agentStatusStore';
import { useOptimizationsPageStore } from './optimizationsPageStore';
import { useOpenedRunsStore } from './openedRunsStore';
import { usePinnedAggregationsStore } from './pinnedAggregationsStore';
import { usePinnedStrategiesStore } from './pinnedStrategiesStore';

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  loadFromStorage: () => void;
  updateUser: (updates: Partial<AuthUser>) => void;
}

/**
 * SHA-71: Clear every per-user cache/store so one account's data can never
 * leak into the next account's session on the same browser tab.
 *
 * Root cause of the bug this fixes: the @tanstack/react-query `QueryClient`
 * is a single module-level singleton for the whole SPA lifetime (see
 * `../queryClient.ts`), and most Zustand stores are plain in-memory stores
 * that also live for the whole SPA lifetime. Neither is tied to the auth
 * token. `logout()` previously only cleared `auth_token`/`auth_user` from
 * localStorage and reset a handful of *selection* ids — the actual cached
 * API responses (history, aggregations, paper sessions, strategies,
 * configs, ...) and several stores holding real per-user entity data
 * (e.g. `backtestStore.currentResult`, `openedRunsStore.opened` full
 * `BacktestSummary` blobs, pinned strategy/aggregation ids) were left
 * completely untouched. A second account logging in immediately afterward
 * (no full page reload) would see the first account's data until every
 * individual query happened to refetch and every store happened to be
 * overwritten by a user action.
 *
 * Three of these stores (`openedRunsStore`, `pinnedAggregationsStore`,
 * `pinnedStrategiesStore`) persist to fixed localStorage keys that are NOT
 * keyed by user id or auth token, so they leak even across a full page
 * reload (e.g. the 401-triggered `window.location.reload()` in
 * `api/client.ts`) — clearing them here (their `set()` calls go through the
 * zustand `persist` middleware, which writes to localStorage synchronously)
 * is required in addition to clearing `auth_token`/`auth_user`.
 *
 * Deliberately NOT touched: anonymous, non-per-user UI preferences such as
 * the chart's log/linear price-scale toggle (`sl-chart-price-scale-mode` in
 * `components/Chart/Chart.tsx`) — those aren't tied to any account and
 * should survive a logout/login exactly like a theme preference would.
 */
function resetPerUserState(): void {
  // Wipes every cached query result (history, aggregations, paper sessions,
  // strategies, configs, optimizations, credentials, ...) regardless of
  // query key namespace.
  queryClient.clear();

  // Entity data + selection state that must not leak between accounts.
  useBacktestStore.getState().clear();
  useConfigStore.getState().reset();
  useConfigurationStore.getState().reset();
  useAggregationStore.getState().reset();
  usePaperTradingStore.getState().reset();
  useSettingsStore.getState().reset();
  usePortfoliosStore.getState().reset();
  useOptimizationStore.getState().reset();
  useOptimizerModalStore.getState().reset();
  useRunBacktestModalStore.getState().close();
  useScannerStore.getState().clearAll();
  useRealtimeEquityStore.getState().reset();
  useAgentStatusStore.getState().reset();
  useOptimizationsPageStore.getState().reset();

  // Persisted-to-localStorage stores — MUST be cleared explicitly since
  // their storage keys are fixed (not keyed by user/token) and therefore
  // survive both an in-tab account switch AND a full page reload.
  useOpenedRunsStore.getState().clearAll();
  usePinnedAggregationsStore.getState().clearAll();
  usePinnedStrategiesStore.getState().clearAll();
}

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  user: null,
  isAuthenticated: false,

  login: (token: string, user: AuthUser) => {
    // Guard the login path (item 4): a new identity can log in without a
    // prior explicit logout() call (e.g. the 401 handler in api/client.ts
    // clears the token and reloads the page, after which the user logs in
    // again in the same fresh tab — the persisted pinned/opened stores
    // would otherwise still carry the previous account's ids). Always
    // start from a clean slate before recording the new session.
    resetPerUserState();

    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));
    set({ token, user, isAuthenticated: true });
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    set({ token: null, user: null, isAuthenticated: false });

    // Clear the react-query cache and reset every per-user store so no
    // stale data or selection bleeds into the next account's session.
    resetPerUserState();

    // Push the root path so the address bar reads / while the login screen is shown.
    window.history.pushState({}, '', '/');
  },

  loadFromStorage: () => {
    const token = localStorage.getItem('auth_token');
    const userStr = localStorage.getItem('auth_user');
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr) as AuthUser;
        set({ token, user, isAuthenticated: true });
      } catch {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
    }
  },

  updateUser: (updates: Partial<AuthUser>) => {
    set((state) => {
      if (!state.user) return state;
      const updatedUser = { ...state.user, ...updates };
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      return { user: updatedUser };
    });
  },
}));
